import { computed, defineAsyncComponent } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";

import mediaObjectApi from "@apis/media.object.api";
import { isEmpty } from "@utils/var.check";
import DateTimeFormatter from "@utils/datetime.formatter";
import type { ApiResponse } from "@apis/api.ts";

import {
  AVAILABILITY,
  LICENSE_TYPE,
  MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION,
  MODIFICATION_RIGHT,
  MODIFICATION_TYPE,
} from "@plugins/media/media.constant";
import { STATUS } from "@modules/manage-resource/service/manage.resource.constant";
import ROLE from "@configs/role";
import type {
  MediaObjectBasic,
  MediaObjectCopyRightModel,
  MediaObjectFormData,
  MediaObjectWorkflowModel,
} from "@plugins/media/types.ts";

export function useMediaObject(editFormData: MediaObjectFormData, error: Record<string, string>) {
  const store = useStore();
  const { t } = useI18n();
  const modal = useModal();
  const toast = useToast();
  const contentSubpage = useContentSubpageInstance();

  const useMediaObject_characterLimit = 500;

  const serverTime = computed(() => store.getters["app/serverTime"] as string | number | Date);

  const useMediaObject_isCopyRightOwnershipLicenseMaterial = computed(
    () => editFormData.owner === MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.LICENSED_MATERIALS.value,
  );

  const useMediaObject_isNonSLS = computed(() => !editFormData.slsUse);

  const useMediaObject_isDisabledLicenceRemark = computed(
    () => !(editFormData.licenseType === LICENSE_TYPE.COPYRIGHT_EXCEPTION_OTHERS.value),
  );

  const useMediaObject_isDisabledModificationRemark = computed(
    () => !(editFormData.modificationType?.includes(MODIFICATION_TYPE.OTHERS.value) === true),
  );

  const useMediaObject_isDisabledLevelSubject = computed(
    () => !(editFormData.availability === AVAILABILITY.RESTRICTED.value),
  );

  const useMediaObject_isDisabledModificationType = computed(
    () => editFormData.modificationRight === MODIFICATION_RIGHT.NO.value,
  );

  const useMediaObject_isMandatoryModificationType = computed(
    () =>
      editFormData.modificationRight === MODIFICATION_RIGHT.NO.value ||
      editFormData.modificationRight === MODIFICATION_RIGHT.YES.value,
  );

  const useMediaObject_isDisabledLicenceDate = computed(
    () => !(editFormData.licenseType === LICENSE_TYPE.TERM.value),
  );

  function useMediaObject_validateEmptyField(formData: MediaObjectFormData): boolean {
    let isValid = true;

    const validateField: (keyof MediaObjectFormData)[] = [
      "owner",
      "copyRightAuthor",
      "authorAcknowledgement",
      "author",
      "authorAttribution",
      "rmsType",
      "licenseType",
      "startDate",
      "endDate",
      "territory",
      "reproduction",
      "downloadableRight",
      "printableRight",
      "modificationType",
      "licensorName",
      "licensorContact",
      "licenseSource",
      "licensorRequest",
      "availability",
      "levelAndStreams",
      "subjects",
      "exceptionDetails",
    ];

    const inputFields: (keyof MediaObjectFormData)[] = [
      "copyRightAuthor",
      "authorAcknowledgement",
      "author",
      "authorAttribution",
      "startDate",
      "endDate",
      "licensorName",
      "licensorContact",
      "licenseSource",
      "licensorRequest",
      "exceptionDetails",
    ];

    const specialConditions: Partial<Record<keyof MediaObjectFormData, () => boolean>> = {
      startDate: () => formData.licenseType !== LICENSE_TYPE.TERM.value,
      endDate: () => formData.licenseType !== LICENSE_TYPE.TERM.value,
      modificationType: () =>
        isEmpty(formData.modificationRight) ||
        formData.modificationRight === MODIFICATION_RIGHT.YES.value ||
        formData.modificationRight === MODIFICATION_RIGHT.NO.value,
      licensorName: () =>
        formData.owner !== MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.LICENSED_MATERIALS.value,
      licensorContact: () =>
        formData.owner !== MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.LICENSED_MATERIALS.value,
      licenseSource: () =>
        formData.owner !== MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.LICENSED_MATERIALS.value,
      licensorRequest: () =>
        formData.owner !== MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.LICENSED_MATERIALS.value,
      levelAndStreams: () => formData.availability !== AVAILABILITY.RESTRICTED.value,
      subjects: () => formData.availability !== AVAILABILITY.RESTRICTED.value,
      reproduction: () => !!formData.slsUse,
      exceptionDetails: () =>
        formData.licenseType !== LICENSE_TYPE.COPYRIGHT_EXCEPTION_OTHERS.value,
    };

    validateField.forEach((key) => {
      const value = formData[key];

      if (isEmpty(value)) {
        if (inputFields.includes(key)) {
          error[key] = t("media_object_field_validation_input_empty") as string;
        } else {
          error[key] = t("media_object_field_validation_select_option") as string;
        }

        if (specialConditions[key] && specialConditions[key]!()) {
          error[key] = "";
        }

        if (error[key]) {
          isValid = false;
        }
      } else {
        error[key] = "";
      }
    });

    return isValid;
  }

  function useMediaObject_validateCharacterLimit(formData: Record<string, unknown>): boolean {
    let isValid = true;

    Object.keys(formData).forEach((key) => {
      const value = formData[key];

      if (typeof value === "string" && value.length > useMediaObject_characterLimit) {
        isValid = false;
        error[key] = t("character_limit_invalid_message", {
          num: useMediaObject_characterLimit,
        }) as string;
      } else {
        error[key] = "";
      }
    });
    return isValid;
  }

  function useMediaObject_validateEndDate(formData: MediaObjectFormData): boolean {
    error.startDate = "";
    error.endDate = "";
    let isValid = true;

    if (formData.licenseType !== LICENSE_TYPE.TERM.value) {
      return true;
    }

    if (!isEmpty(formData.startDate) && !isEmpty(formData.endDate)) {
      const startDate = new Date(formData.startDate as string | number | Date);
      const endDate = new Date(formData.endDate as string | number | Date);
      const currentDate = new Date(serverTime.value as string | number | Date);
      currentDate.setHours(0, 0, 0, 0);

      if (endDate <= startDate) {
        isValid = false;
        error.endDate = t(
          "media_object_field_end_date_after_start_date_validation_error",
        ) as string;
      }
      if (endDate <= currentDate) {
        isValid = false;
        error.endDate = t("media_object_field_end_date_earlier_now_validation_error") as string;
      }
    }
    return isValid;
  }

  function useMediaObject_saveMediaObjectIPDetails(
    requestData: MediaObjectCopyRightModel,
  ): Promise<ApiResponse<MediaObjectCopyRightModel>> {
    return new Promise<ApiResponse<MediaObjectCopyRightModel>>((resolve, reject) => {
      mediaObjectApi
        .saveMediaObjectIPDetails(requestData)
        .then((response: ApiResponse<MediaObjectCopyRightModel>) => {
          if (response.code === "ok") {
            resolve(response);
          } else {
            reject("error");
          }
        });
    });
  }

  function useMediaObject_showSubmitForApprovalModal(
    actionPrimary: () => void,
    actionSecondary: () => void,
  ): void {
    modal.showMessage("info", {
      title: t("media_object_submit_for_approval_toast_title"),
      content: t("media_object_submit_for_approval_toast_message"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      use_secondaryButton: true,
      actionPrimary,
      actionSecondary,
    });
  }

  function useMediaObject_submitForApproval(
    uuid: string,
    isDisplayCommonAction: boolean,
  ): Promise<ApiResponse<MediaObjectWorkflowModel>> {
    return new Promise<ApiResponse<MediaObjectWorkflowModel>>((resolve) => {
      mediaObjectApi
        .updateMediaObjectStatusToSubmit(uuid)
        .then((response: ApiResponse<MediaObjectWorkflowModel>) => {
          if (response.code === "ok") {
            if (isDisplayCommonAction) {
              toast.show("success", {
                title: t("media_object_submit_success_toast_message", { count: 1 }),
              });
            }
          } else {
            if (isDisplayCommonAction) {
              const MediaErrorContentSubpage = defineAsyncComponent(
                () => import("@modules/manage-resource/components/MediaErrorContentSubpage.vue"),
              );
              contentSubpage.show(MediaErrorContentSubpage, {
                uuidList: [uuid],
                hasParentSubpage: false,
                actionType: "submitForApprove",
              });
            }
          }
          resolve(response);
        });
    });
  }

  function useMediaObject_showApproveModal(
    actionPrimary: () => void,
    actionSecondary: () => void,
  ): void {
    modal.showMessage("info", {
      title: t("media_object_approve_confirmation_title_label"),
      content: t("media_object_approve_confirmation_subtitle_label"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      use_secondaryButton: true,
      actionPrimary,
      actionSecondary,
    });
  }

  function useMediaObject_approve(
    uuid: string,
    isDisplayCommonAction: boolean,
    errorSubpageOptions: { hasParentSubpage?: boolean; parentSubpageName?: string } = {},
  ): Promise<ApiResponse<MediaObjectWorkflowModel>> {
    return new Promise<ApiResponse<MediaObjectWorkflowModel>>((resolve) => {
      mediaObjectApi
        .updateMediaObjectStatusToApproved(uuid)
        .then((response: ApiResponse<MediaObjectWorkflowModel>) => {
          if (response.code === "ok") {
            if (isDisplayCommonAction) {
              toast.show("success", {
                title: t("media_object_publish_success_toast_message", { count: 1 }),
              });
            }
          } else {
            if (isDisplayCommonAction) {
              const { hasParentSubpage = false, parentSubpageName = "" } = errorSubpageOptions;
              const MediaErrorContentSubpage = defineAsyncComponent(
                () => import("@modules/manage-resource/components/MediaErrorContentSubpage.vue"),
              );
              contentSubpage.show(MediaErrorContentSubpage, {
                uuidList: [uuid],
                hasParentSubpage,
                parentSubpageName,
                actionType: "approve",
              });
            }
          }
          resolve(response);
        });
    });
  }

  function useMediaObject_showRejectModal(
    actionPrimary: () => void,
    actionSecondary: () => void,
  ): void {
    modal.showMessage("info", {
      title: t("media_object_reject_confirmation_title_label"),
      content: t("media_object_reject_confirmation_content"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      use_secondaryButton: true,
      actionPrimary,
      actionSecondary,
    });
  }

  function useMediaObject_reject(uuid: string): Promise<ApiResponse<MediaObjectWorkflowModel>> {
    return new Promise<ApiResponse<MediaObjectWorkflowModel>>((resolve, reject) => {
      mediaObjectApi
        .updateMediaObjectStatusToRejected(uuid)
        .then((response: ApiResponse<MediaObjectWorkflowModel>) => {
          if (response.code === "ok") {
            toast.show("success", {
              title: t("media_object_reject_success_toast_message", { count: 1 }),
            });
            resolve(response);
          } else {
            reject("error");
          }
        });
    });
  }

  function useMediaObject_showUnpublishModal(
    actionPrimary: () => void,
    actionSecondary: () => void,
  ): void {
    modal.showMessage("info", {
      title: t("media_object_unpublish_confirmation_title_label"),
      content: t("media_object_unpublish_confirmation_content"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      use_secondaryButton: true,
      actionPrimary,
      actionSecondary,
    });
  }

  function useMediaObject_unpublish(uuid: string): Promise<ApiResponse<MediaObjectWorkflowModel>> {
    return new Promise<ApiResponse<MediaObjectWorkflowModel>>((resolve, reject) => {
      mediaObjectApi
        .updateMediaObjectStatusToUnpublish(uuid)
        .then((response: ApiResponse<MediaObjectWorkflowModel>) => {
          if (response.code === "ok") {
            toast.show("success", {
              title: t("media_object_unpublish_success_toast_message", { count: 1 }),
            });
            resolve(response);
          } else {
            reject("error");
          }
        });
    });
  }

  function useMediaObject_getExpirationDate(
    endDate: string | number | Date | undefined,
    licenseType: string | number | undefined,
  ): string {
    if (licenseType === LICENSE_TYPE.PERPETUAL.value) {
      return t("media_object_data_table_end_date_perpetual") as string;
    }
    if (!isEmpty(endDate)) {
      return DateTimeFormatter.formatLocalDateTimeToDateOnly(endDate);
    }
    return "-";
  }

  function useMediaObject_canMassApprove(
    mediaObjects: MediaObjectBasic[] = [],
    userRoles: (string | number)[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return mediaObjects.every((mediaObject) =>
      useMediaObject_canApprove(mediaObject, userRoles, ownerGroupIds),
    );
  }

  function useMediaObject_canMassSubmitForApproval(
    mediaObjects: MediaObjectBasic[] = [],
    userRoles: (string | number)[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return mediaObjects.every((mediaObject) =>
      useMediaObject_canSubmitForApproval(mediaObject, userRoles, ownerGroupIds),
    );
  }

  function hasSubmitForApprovalStatus(mediaObject: MediaObjectBasic): boolean {
    return [STATUS.DRAFT.value, STATUS.REJECTED.value].includes(mediaObject.status);
  }

  function hasApproveStatus(mediaObject: MediaObjectBasic): boolean {
    return [STATUS.DRAFT.value, STATUS.PENDING_APPROVAL.value, STATUS.REJECTED.value].includes(
      mediaObject.status,
    );
  }

  function isInOwnerGroup(
    mediaObject: MediaObjectBasic,
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return ownerGroupIds.includes(mediaObject.ownerGroupId);
  }

  function useMediaObject_canSubmitForApprovalByRole(userRoles: (string | number)[] = []): boolean {
    return userRoles.includes(ROLE.CONTENT_OFFICER);
  }

  function useMediaObject_canApproveByRole(userRoles: (string | number)[] = []): boolean {
    return userRoles.includes(ROLE.CONTENT_APPROVER);
  }

  function useMediaObject_canSubmitForApprovalByState(
    mediaObject: MediaObjectBasic,
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return hasSubmitForApprovalStatus(mediaObject) && isInOwnerGroup(mediaObject, ownerGroupIds);
  }

  function useMediaObject_canApproveByState(
    mediaObject: MediaObjectBasic,
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return hasApproveStatus(mediaObject) && isInOwnerGroup(mediaObject, ownerGroupIds);
  }

  // Unlike the composed mass predicates below, an empty selection is not permitted:
  // nothing selected means there is no state for the action to act on.
  function useMediaObject_canMassSubmitForApprovalByState(
    mediaObjects: MediaObjectBasic[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return (
      mediaObjects.length > 0 &&
      mediaObjects.every((mediaObject) =>
        useMediaObject_canSubmitForApprovalByState(mediaObject, ownerGroupIds),
      )
    );
  }

  function useMediaObject_canMassApproveByState(
    mediaObjects: MediaObjectBasic[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return (
      mediaObjects.length > 0 &&
      mediaObjects.every((mediaObject) =>
        useMediaObject_canApproveByState(mediaObject, ownerGroupIds),
      )
    );
  }

  function useMediaObject_canSubmitForApproval(
    mediaObject: MediaObjectBasic,
    userRoles: (string | number)[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return (
      hasSubmitForApprovalStatus(mediaObject) &&
      useMediaObject_canSubmitForApprovalByRole(userRoles) &&
      isInOwnerGroup(mediaObject, ownerGroupIds)
    );
  }

  function useMediaObject_canApprove(
    mediaObject: MediaObjectBasic,
    userRoles: (string | number)[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return (
      hasApproveStatus(mediaObject) &&
      useMediaObject_canApproveByRole(userRoles) &&
      isInOwnerGroup(mediaObject, ownerGroupIds)
    );
  }

  function useMediaObject_canReject(
    mediaObject: MediaObjectBasic,
    userRoles: (string | number)[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return (
      mediaObject.status === STATUS.PENDING_APPROVAL.value &&
      userRoles.includes(ROLE.CONTENT_APPROVER) &&
      ownerGroupIds.includes(mediaObject.ownerGroupId)
    );
  }

  function useMediaObject_canUnpublish(
    mediaObject: MediaObjectBasic,
    userRoles: (string | number)[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    return (
      mediaObject.status === STATUS.APPROVED.value &&
      userRoles.includes(ROLE.CONTENT_APPROVER) &&
      ownerGroupIds.includes(mediaObject.ownerGroupId)
    );
  }

  function useMediaObject_canDelete(
    mediaObject: MediaObjectBasic,
    userRoles: (string | number)[] = [],
    ownerGroupIds: (string | number)[] = [],
  ): boolean {
    if (userRoles.includes(ROLE.CONTENT_APPROVER)) {
      return (
        [STATUS.DRAFT.value, STATUS.REJECTED.value, STATUS.UNPUBLISHED.value].includes(
          mediaObject.status,
        ) && ownerGroupIds.includes(mediaObject.ownerGroupId)
      );
    }
    if (userRoles.includes(ROLE.CONTENT_OFFICER)) {
      return (
        [STATUS.DRAFT.value, STATUS.REJECTED.value, STATUS.UNPUBLISHED.value].includes(
          mediaObject.status,
        ) && ownerGroupIds.includes(mediaObject.ownerGroupId)
      );
    }
    return false;
  }

  return {
    // state
    useMediaObject_characterLimit,

    // computed
    useMediaObject_isCopyRightOwnershipLicenseMaterial,
    useMediaObject_isNonSLS,
    useMediaObject_isDisabledLicenceRemark,
    useMediaObject_isDisabledModificationRemark,
    useMediaObject_isDisabledLevelSubject,
    useMediaObject_isDisabledModificationType,
    useMediaObject_isMandatoryModificationType,
    useMediaObject_isDisabledLicenceDate,

    // methods
    useMediaObject_validateEmptyField,
    useMediaObject_validateCharacterLimit,
    useMediaObject_validateEndDate,
    useMediaObject_saveMediaObjectIPDetails,
    useMediaObject_showSubmitForApprovalModal,
    useMediaObject_submitForApproval,
    useMediaObject_showApproveModal,
    useMediaObject_approve,
    useMediaObject_showRejectModal,
    useMediaObject_reject,
    useMediaObject_showUnpublishModal,
    useMediaObject_unpublish,
    useMediaObject_getExpirationDate,
    useMediaObject_canMassApprove,
    useMediaObject_canMassSubmitForApproval,
    useMediaObject_canSubmitForApproval,
    useMediaObject_canApprove,
    useMediaObject_canSubmitForApprovalByRole,
    useMediaObject_canApproveByRole,
    useMediaObject_canSubmitForApprovalByState,
    useMediaObject_canApproveByState,
    useMediaObject_canMassSubmitForApprovalByState,
    useMediaObject_canMassApproveByState,
    useMediaObject_canReject,
    useMediaObject_canUnpublish,
    useMediaObject_canDelete,
  };
}
